/**
 * Client telemetry — the client half. Tier 0 (version census) and tier 1
 * (operational beacons). See `docs/CLIENT_TELEMETRY.md`.
 *
 * WHAT THIS IS NOT: it is not an analytics SDK, it does not batch, it does not
 * retry, it opens no connection and it has no queue. It maintains a small
 * in-memory snapshot of "what build am I and how is sync doing", and serialises
 * it into ONE header that rides requests the app already makes. If nothing ever
 * calls the setters, the app behaves exactly as it did before.
 *
 * WHAT IT MUST NEVER CONTAIN: anything about the person. No user id, no email,
 * no device identifier, no free text the user typed. Every field below is a
 * property of the BUILD or of the sync machinery. The server-side parser
 * enforces this a second time by whitelisting the values it accepts.
 */

import { CLIENT_INFO_HEADER } from "@aula/client";

export { CLIENT_INFO_HEADER };

/** Distribution channel this copy of the app runs in. */
export type ClientPlatform = "web" | "android" | "ios" | "desktop";

/** Outcome of the most recent Service Worker update attempt. */
export type ServiceWorkerUpdateState = "none" | "pending" | "applied" | "failed";

/** Immutable half: what this build IS. Set once at boot. */
export interface ClientBuildIdentity {
  /** Product semver, e.g. `0.2.0`. Moves for copy changes — for humans. */
  appVersion: string | null;
  /** Short git hash of the build. Distinguishes two deploys of one semver. */
  buildHash: string | null;
  platform: ClientPlatform | null;
  /**
   * API contract version — a monotonic integer that moves ONLY when the
   * request/response shape changes. This is the field that decides when
   * `min_supported` can be raised (infra/API_COMPAT_POLICY.md).
   *
   * This repo HAS the contract feature (single source `api-contract.json` at the
   * repo root, read by `build.rs` and `vite.config.ts`), so a current build
   * reports a real number. `null` therefore means "this device runs a build made
   * BEFORE the feature landed", not "this product has no contract": the census
   * reports it as "contract unknown" rather than guessing.
   */
  contractVersion: number | null;
}

/** Mutable half: how this device is DOING. */
export interface ClientBeacons {
  /** Epoch ms of the last successful sync, or `null` if it never synced. */
  lastSyncedAtMs: number | null;
  /** Pending write-queue depth (operations not yet acknowledged). */
  queueDepth: number | null;
  /** Count of 401s on requests the client believed were authenticated. */
  authTransportFailures: number;
  serviceWorkerUpdate: ServiceWorkerUpdateState;
}

const identity: ClientBuildIdentity = {
  appVersion: null,
  buildHash: null,
  platform: null,
  contractVersion: null,
};

const beacons: ClientBeacons = {
  lastSyncedAtMs: null,
  queueDepth: null,
  authTransportFailures: 0,
  serviceWorkerUpdate: "none",
};

/**
 * Declares what this build is. Called once at boot by the app shell, which is
 * the only layer that knows `import.meta.env` and the runtime platform.
 */
export function setClientBuildIdentity(next: Partial<ClientBuildIdentity>): void {
  Object.assign(identity, next);
}

/** Current build identity (read-only copy). Exported for the diagnostics screen. */
export function getClientBuildIdentity(): ClientBuildIdentity {
  return { ...identity };
}

/** Feeds the sync beacons. Called from the PowerSync status listener. */
export function setSyncBeacons(next: {
  lastSyncedAtMs?: number | null;
  queueDepth?: number | null;
}): void {
  if (next.lastSyncedAtMs !== undefined) {
    beacons.lastSyncedAtMs = next.lastSyncedAtMs;
  }
  if (next.queueDepth !== undefined) {
    beacons.queueDepth = next.queueDepth;
  }
}

/**
 * Increments the auth-transport failure counter.
 *
 * Saturates rather than growing without bound: the panel asks "are devices
 * hitting this at all", and the difference between 500 and 501 is not a
 * question anyone has. Capping also keeps the header a fixed size.
 */
export function recordAuthTransportFailure(): void {
  beacons.authTransportFailures = Math.min(beacons.authTransportFailures + 1, 9999);
}

/** Records the outcome of a Service Worker update attempt. */
export function setServiceWorkerUpdate(state: ServiceWorkerUpdateState): void {
  beacons.serviceWorkerUpdate = state;
}

/** Current beacons (read-only copy). Exported for tests and diagnostics. */
export function getClientBeacons(): ClientBeacons {
  return { ...beacons };
}

/**
 * Resets everything. Tests only — module state would otherwise leak between
 * cases and make one test's assertion depend on another's ordering.
 */
export function resetClientTelemetryForTests(): void {
  identity.appVersion = null;
  identity.buildHash = null;
  identity.platform = null;
  identity.contractVersion = null;
  beacons.lastSyncedAtMs = null;
  beacons.queueDepth = null;
  beacons.authTransportFailures = 0;
  beacons.serviceWorkerUpdate = "none";
}

/** Keeps a value inside the grammar: no `;`, no `=`, no whitespace, bounded. */
function sanitize(value: string): string | null {
  const cleaned = value.replaceAll(/[^\w.+-]/g, "").slice(0, 64);
  return cleaned === "" ? null : cleaned;
}

/**
 * Serialises identity + beacons into the header value. PURE — the clock is a
 * parameter, so the golden-value test below is not time-dependent.
 *
 * Grammar (mirrors `parse_client_info` in
 * `packages/core-rust/src/db/client_telemetry.rs`): `key=value` pairs joined by
 * `"; "`. Deliberately not JSON — a header value has no dependable escaping
 * story, and one bad field would take the whole payload down with it. Fields
 * whose value is unknown are OMITTED rather than sent as `unknown`, so the
 * server counts them as absent instead of inventing a bucket.
 *
 * The two beacon fields `auth_fail` and `sw` are ALWAYS emitted: "zero auth
 * failures" and "no Service Worker update" are real answers, and a device that
 * reports them while knowing nothing about its own version is a genuinely
 * different situation from a device that reports nothing at all. The census
 * counts those two cases separately (`versionUnknownDevices` vs
 * `unknownDevices`) precisely so the difference stays visible.
 */
export function formatClientInfoHeader(
  build: ClientBuildIdentity,
  state: ClientBeacons,
  nowMs: number,
): string {
  const parts: string[] = [];

  const version = build.appVersion === null ? null : sanitize(build.appVersion);
  if (version !== null) {
    parts.push(`v=${version}`);
  }
  const hash = build.buildHash === null ? null : sanitize(build.buildHash);
  if (hash !== null) {
    parts.push(`build=${hash}`);
  }
  if (build.platform !== null) {
    parts.push(`platform=${build.platform}`);
  }
  if (build.contractVersion !== null && Number.isSafeInteger(build.contractVersion)) {
    parts.push(`contract=${build.contractVersion}`);
  }

  if (state.lastSyncedAtMs !== null) {
    // Floor at 0: a clock skew that puts the last sync in the future must not
    // emit a negative age, which the server would then drop as junk.
    const ageSeconds = Math.max(0, Math.floor((nowMs - state.lastSyncedAtMs) / 1000));
    parts.push(`sync_age=${ageSeconds}`);
  }
  if (state.queueDepth !== null && Number.isSafeInteger(state.queueDepth)) {
    parts.push(`queue=${Math.max(0, state.queueDepth)}`);
  }
  parts.push(`auth_fail=${state.authTransportFailures}`);
  parts.push(`sw=${state.serviceWorkerUpdate}`);

  return parts.join("; ");
}

/**
 * Builds the header from the current module state. This is what gets wired into
 * the API client as `getClientInfo`.
 */
export function buildClientInfoHeader(nowMs: number = Date.now()): string {
  return formatClientInfoHeader(identity, beacons, nowMs);
}
